import { useCallback, useState } from 'react';

/**
 * Editing the lines of a document. Each line carries a stable key so React
 * keeps focus in the right box when a row above it is removed — without it, a
 * cashier typing a weight loses the cursor mid-number.
 */
export function useLines<T>(blank: () => T): {
  lines: (T & { key: string })[];
  add: () => void;
  remove: (key: string) => void;
  update: (key: string, patch: Partial<T>) => void;
  reset: () => void;
  /** Put a whole set of lines back — used to re-open a document for correction. */
  replace: (values: T[]) => void;
  values: T[];
} {
  const makeLine = useCallback(() => ({ ...blank(), key: crypto.randomUUID() }), [blank]);
  const [lines, setLines] = useState<(T & { key: string })[]>(() => [makeLine()]);

  return {
    lines,
    add: () => setLines((current) => [...current, makeLine()]),
    remove: (key) => setLines((current) => (current.length === 1 ? current : current.filter((l) => l.key !== key))),
    update: (key, patch) =>
      setLines((current) => current.map((l) => (l.key === key ? { ...l, ...patch } : l))),
    reset: () => setLines([makeLine()]),
    replace: (values) =>
      setLines(values.length > 0 ? values.map((v) => ({ ...v, key: crypto.randomUUID() })) : [makeLine()]),
    values: lines.map(({ key: _key, ...rest }) => rest as unknown as T),
  };
}
